// Métriques de la recherche publique pour la page réservée aux administrateurs :
// la latence jour par jour (mêmes compteurs que /api/metrics/search) et le top des
// termes cherchés par les visiteurs sur la fenêtre glissante de sept jours.
//
// IMPORTANT : passe par l'instance `axios` partagée (JAMAIS fetch brut) pour que
// l'interceptor Auth0AxiosBridge attache le Bearer admin.
import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import type { SearchUsagePoint } from "./useSearchUsage";

export interface SearchTermPoint {
	term: string;
	count: number;
	/** Parmi ces recherches, combien n'ont renvoyé aucun résultat. */
	zeroResults: number;
}

export interface SearchMetrics {
	days: SearchUsagePoint[];
	today: SearchUsagePoint;
	topTerms: SearchTermPoint[];
	termWindowDays: number;
}

const apiBase = (process.env.REACT_APP_API_URL ?? "").replace(/\/+$/, "");

const VIDE = { requests: 0, failures: 0, averageMs: 0, maxMs: 0 };

/** Une réponse partielle ne doit pas casser la page : chaque champ a un repli. */
const normalizePoint = (point: Partial<SearchUsagePoint> | undefined, fallbackDate: string): SearchUsagePoint => ({
	date: point?.date ?? fallbackDate,
	search: { ...VIDE, ...(point?.search ?? {}) },
	suggest: { ...VIDE, ...(point?.suggest ?? {}) },
});

const normalizeTerm = (t: Partial<SearchTermPoint> | undefined): SearchTermPoint => ({
	term: t?.term ?? "",
	count: t?.count ?? 0,
	zeroResults: t?.zeroResults ?? 0,
});

/**
 * Les métriques de recherche, rafraîchies toutes les 60 s. `error` reste nul tant
 * qu'un jeu de données a déjà été affiché : une coupure passagère ne doit pas
 * vider le tableau de bord.
 */
export function useSearchMetrics(days = 14, pollMs = 60000) {
	const [metrics, setMetrics] = useState<SearchMetrics | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const mountedRef = useRef(true);
	const hasDataRef = useRef(false);

	const refresh = useCallback(async () => {
		try {
			const r = await axios.get<SearchMetrics>(`${apiBase}/api/search-metrics?days=${days}`, {
				withCredentials: true,
			});
			if (!mountedRef.current) return;
			const today = new Date().toISOString().slice(0, 10);
			setMetrics({
				days: (r.data?.days ?? []).map((point) => normalizePoint(point, today)),
				today: normalizePoint(r.data?.today, today),
				topTerms: (r.data?.topTerms ?? []).map(normalizeTerm).filter((t) => t.term.length > 0),
				termWindowDays: r.data?.termWindowDays ?? 7,
			});
			hasDataRef.current = true;
			setError(null);
		} catch (err) {
			console.error(err);
			if (mountedRef.current && !hasDataRef.current) {
				setError("Les métriques de la recherche n'ont pas pu être lues.");
			}
		} finally {
			if (mountedRef.current) setLoading(false);
		}
	}, [days]);

	useEffect(() => {
		mountedRef.current = true;
		void refresh();
		const timer = setInterval(() => void refresh(), pollMs);
		return () => {
			mountedRef.current = false;
			clearInterval(timer);
		};
	}, [refresh, pollMs]);

	return { metrics, loading, error, refresh };
}
